import { BCMSCurrency, BCMSDate, BCMSDefaultModel, BCMSRecord, ModalOpen, useBCMSModal, useGlobalController, useRoute, useRouteData, useWorkflowStep } from "@bcm-dev/bcms-vite";
import ButtonContainer from "compid/shared/containers/ButtonContainer";
import PageLayoutContainer from "compid/shared/containers/PageLayoutContainer";
import CenterColumnContainer from "compid/shared/containers/PageLayoutContainer/containers/CenterColumnContainer";
import PageView from "compid/shared/containers/PageViewContainer";
import AutopayFormView from "compid/shared/views/AutopayFormView";
import BillerView from "compid/shared/views/BillerView";
import UIButton from "compid/ui/UIButton";
import { getDefaultAutopay, getWorkflowSteps } from "compid/workflows/AddAutoPay";
import { Account, Autopay, Bill, Biller, Payment } from "model";
import "./styles.css";

export default function AddAutoPayPage(props) {
    const controller = useGlobalController();
    // Get the workflow step and initalize model classes
    const workflowStep = useWorkflowStep(getDefaultAutopay, getWorkflowSteps);
    BCMSRecord.globalControllerFor([ Autopay, Bill, Biller, Payment ], controller);
    
    const biller = workflowStep.read("biller");
    const autopay = workflowStep.read("autopay");
    const payment = workflowStep.read("payment");
    const bill = workflowStep.read("bill");
    const { branding } = workflowStep.model ?? {};
    const hasAutopays = Autopay.initAll().filter(item => item.billerID === biller.id, []).length > 0;
    const showShortForm = !hasAutopays && biller.eBillStatus === "Active";
    const withdrawDate = Payment.getWithdrawOnDate(autopay.deliverBy, biller.paymentType, branding);
    const hasRealTimeGoodFunds = Payment.hasRealTimeGoodFunds(biller.paymentType, branding);
    let autopayAmount = BCMSCurrency.init(0);
    const autopayAlwaysPay = autopay.alwaysPay;

    // "autopay.alwaysPay" type varies when toggling the "Always pay" dropdown.
    if(autopayAlwaysPay instanceof BCMSCurrency) {
        autopayAmount = autopayAlwaysPay;
    } else if(autopayAlwaysPay === "Amount due") {
        autopayAmount = autopay.maximum;
    } else if(autopayAlwaysPay === "Fixed amount") {
        autopayAmount = autopay.fixed;
    }

    const submit = () => {
        // Set the defaults for the short form
        autopay.alwaysPay = showShortForm && autopay.alwaysPay instanceof BCMSCurrency
            ? "Amount due" : autopay.alwaysPay;
        autopay.deliverBy = showShortForm && autopay.deliverBy instanceof BCMSDate
            ? "Due date" : autopay.deliverBy;

        let amount;
        // Check which amount should be used
        if (autopay.alwaysPay === "Amount due") { amount = bill.amount; }
        else if (autopay.alwaysPay === "Fixed amount") { amount = autopay.fixed; }
        else { amount = autopay.alwaysPay; }

        const defaultAutopay = BCMSDefaultModel.autopay();
        autopay.frequency = autopay.frequency !== undefined ? autopay.frequency : defaultAutopay.frequency;
        autopay.duration = autopay.duration !== undefined ? autopay.duration : defaultAutopay.duration;
        const updatedAutopay = Autopay.init(BCMSDefaultModel.mergeRecords(autopay, defaultAutopay));
        const numberOfPaymentsNumeric = parseFloat(updatedAutopay.numberOfPayments);
        if(numberOfPaymentsNumeric === 0) updatedAutopay.numberOfPayments = defaultAutopay.numberOfPayments;

        const date = updatedAutopay.deliverBy instanceof BCMSDate ? updatedAutopay.deliverBy : BCMSDate.init().nextBusinessDay();
        amount = amount.value === 0 ? defaultAutopay.alwaysPay : amount;
        let payFromAccountRecord = autopay.payFrom.record;

        // If "real time good funds" is on, then subtract the autopay amount.
        if(hasRealTimeGoodFunds && withdrawDate.isToday()) {
            const autopayAmountNumeric = parseFloat(amount.amount);
            let payFromAccountBalance = parseFloat(payFromAccountRecord.accountBalance);
            payFromAccountRecord = { ...payFromAccountRecord, accountBalance: payFromAccountBalance - autopayAmountNumeric }
        }

        workflowStep.update(["payment", "autopay"], autopay);
        workflowStep.update(["payment", "amount"], amount);
        workflowStep.update(["payment", "date"], date);
        workflowStep.update(["payment", "hasRelatedAutopay"], true);
        workflowStep.update(["biller", "autopay"], updatedAutopay);
        workflowStep.update(["bill", "payment"], payment);

        // Update the global model
        BCMSRecord.batch([
            Account.update({ ...payFromAccountRecord }),
            Autopay.create(updatedAutopay),
            Biller.update(biller),
            Payment.create(payment),
            Bill.update(bill)
        ], workflowStep.globalController);

        // Route to the next step
        try {
            window.scrollTo(0, 0);
            workflowStep.workflow.nextStep(workflowStep.getStepState);
        } catch (error) {
            workflowStep.navigate(`/addAutopayWorkflow/${biller.id}/${workflowStep.currentStep + 1}`, { state: workflowStep.getStepState });
        }
    };

    return (

        <PageView className="add-autopay-page">
            <p className="page-sub-header semi-bold-text">ADD AUTOPAY</p>

            <PageLayoutContainer>
                <div className="flex-container flex-vertical flex-width">
                    <div className="flex-height flex-container flex-vertical upper-content-container">
                        <BillerView biller={biller} />
                    </div>

                    <div className="flex-height flex-width flex-container all-center">
                        <CenterColumnContainer>
                            <AutopayFormView>
                                <AutoPayBottomButtons autopay={ autopay } hasRealTimeGoodFunds={ hasRealTimeGoodFunds } submit={submit} />
                            </AutopayFormView>
                        </CenterColumnContainer>
                    </div>
                </div>
            </PageLayoutContainer>
        </PageView>
    );
}

function AutoPayBottomButtons(props) {
    const navigate = useRoute();
    const routeData = useRouteData();
    const autopay = props.autopay;
    const defaultAutopay = BCMSDefaultModel.autopay();
    let autoPayAmount = parseFloat(autopay.alwaysPay.amount);
    autoPayAmount = autoPayAmount === 0 ? parseFloat(defaultAutopay.alwaysPay.amount) : autoPayAmount;
    const payFromAccount = autopay.payFrom;
    const payFromAccountAccountBalance = parseFloat(payFromAccount.balance.amount);
    const hasRealTimeGoodFunds = props.hasRealTimeGoodFunds;
    const { open } = useBCMSModal();

    // "Leave" button is clicked in the "Are you sure you want to leave?" modal.
    const onLeave = () => {
        if (routeData.route === null) {
            navigate("/PayBills");
        }
        else {
            navigate(-1);
        }
    };

    // A modal may open depending on branding.
    const onAddAutopayClick = () => {
        // to do: check if eBills are set since rtgf branding may not apply for that.
        if(hasRealTimeGoodFunds) {
            if(autoPayAmount > payFromAccountAccountBalance) {
                open("InsufficientFundsModal", { payAmount: autopay.alwaysPay, payFrom: payFromAccount, onModalPayButtonClick: props.submit });
            } else {
                props.submit();
            }
        } else {
            props.submit();
        }
    }

    return (
        <ButtonContainer className="form-button-container button-container flex-container flex-horizontal add-autopay-button-container">
            <UIButton className="secondary-button">
                <ModalOpen id="WantToLeaveModal" data={{ onLeave: onLeave }}>Cancel</ModalOpen>
            </UIButton>
            <UIButton className="primary-button" onClick={ onAddAutopayClick } dataPoint="Add_Autopay_Submit_Btn">Add Autopay</UIButton>
        </ButtonContainer>
    );
}